import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { User, AuthState } from '../types';

interface AuthContextType extends AuthState {
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, name: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

/**
 * AuthProvider handles authentication state for the application
 * Using token-based authentication stored in localStorage for cross-origin compatibility
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [state, setState] = useState<AuthState>({
        user: null,
        token: null,
        loading: true,
        error: null
    });

    useEffect(() => {
        const initAuth = async () => {
            try {
                // Check if we have a stored token
                const token = localStorage.getItem('token');
                if (token) {
                    // Set the token in API headers
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    
                    // Validate token with server
                    const response = await api.get<{ isValid: boolean; user: User }>('/auth/validate');
                    
                    if (response.data.isValid) {
                        setState({
                            user: response.data.user,
                            token: token,
                            loading: false,
                            error: null
                        });
                    } else {
                        // Token invalid, clear it
                        localStorage.removeItem('token');
                        delete api.defaults.headers.common['Authorization'];
                        setState({
                            user: null,
                            token: null,
                            loading: false,
                            error: null
                        });
                    }
                } else {
                    setState({
                        user: null,
                        token: null,
                        loading: false,
                        error: null
                    });
                }
            } catch (error) {
                console.error('Auth validation error:', error);
                // Clear invalid token
                localStorage.removeItem('token');
                delete api.defaults.headers.common['Authorization'];
                setState({
                    user: null,
                    token: null,
                    loading: false,
                    error: null
                });
            }
        };

        initAuth();
    }, []);

    const login = async (email: string, password: string) => {
        try {
            const response = await api.post<{ user: User; token: string }>('/auth/login', {
                email,
                password
            });

            const { user, token } = response.data;

            // Store token in localStorage and set in API headers
            localStorage.setItem('token', token);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            setState({
                user,
                token,
                loading: false,
                error: null
            });
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: 'Invalid credentials'
            }));
            throw error;
        }
    };

    const register = async (email: string, password: string, name: string) => {
        try {
            const response = await api.post<{ user: User; token: string }>('/auth/register', {
                email,
                password,
                username: name
            });

            const { user, token } = response.data;

            // Store token in localStorage and set in API headers
            localStorage.setItem('token', token);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            setState({
                user,
                token,
                loading: false,
                error: null
            });
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: 'Registration failed'
            }));
            throw error;
        }
    };

    const logout = async () => {
        try {
            // Call the server to invalidate the session
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear token from localStorage and API headers
            localStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];
            setState({
                user: null,
                token: null,
                loading: false,
                error: null
            });
        }
    };

    return (
        <AuthContext.Provider value={{ ...state, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
};
